import {Input} from 'antd'
import {createValue} from 'components/form/utils'
import _ from 'lodash'
import {getCacheSelector} from 'modules/asyncCache/selectors'
import useTranslate from 'modules/local/useTranslate'
import withTranslate from 'modules/local/withTranslate'
import React, {useContext} from 'react'
import {connect} from 'react-redux'
import {compose, mapProps, pure} from 'recompose'
import {SelectWidget} from 'views/AppLocals/custom/ReactSelectComponents'
import LinkedPages from 'views/Organization/Settings/Profile/LinkedPages'
import {NullAsDefault, renderIfElse, shallowDiff} from 'views/Shared'
import OrganizationContainerContext from '../OrganizationContainerContext'
import {PartialEditGroupWrapper} from "../../../../components/form/PartialEditForm";
import Translate from "../../../../modules/local/Translate";
import {useOrganizationApi} from "../functions/useOrganizationApi";
import {ItemInformation} from 'components/common/ItemInformation'
import {runRules, urlFormat} from "../../../../components/form/ValidateForm";

const withSelectWidget =
    (Component) =>
        ({name, value, data_object, onChange = NullAsDefault, withProps = () => ({})}) => {
            const t = useTranslate()
            const options = Object.keys(data_object).map((key) => ({
                value: key,
                label: data_object[key],
            }))
            return (
                <Component
                    {...{
                        name,
                        options,
                        showSearch: true,
                        allowClear: true,
                        className: 'w-full',
                        optionFilterProp: 'label',
                        value: value || undefined,
                        placeholder: t('---Choose One---'),
                        onChange: (value) => {
                            onChange(createValue(name, _.isUndefined(value) ? null : value))
                        },
                        ...(withProps() || {}),
                    }}
                />
            )
        }

const formEntities = {
    tax_code: {
        title: 'tax code',
        name: 'tax_code',
        component: compose(
            withTranslate,
            mapProps(({name, onChange, value, translate}) => ({
                name,
                value,
                rows: 4,
                onChange,
                placeholder: translate('tax code'),
            })),
            pure
        )(Input),
    },
    linked_pages: {
        title: 'Linked Pages',
        name: 'linked_pages',
        component: compose(
            mapProps(({name, value, onChange}) => ({
                data: value,
                classNames: ['flex items-center gap-3'],
                renderWrapper: ({children}) => (
                    <div className="flex flex-col gap-3">{children}</div>
                ),
                onChange: (value) => {
                    onChange(createValue(name, value))
                },
            })),
            pure
        )(LinkedPages),
    },
    number_of_employees: {
        title: 'Number of Employees',
        name: 'number_of_employees',
        component: compose(
            connect((state) => ({
                data_object: _.get(
                    getCacheSelector(state, 'appConfig'),
                    'number_of_employees',
                    []
                ),
            })),
            withSelectWidget,
            pure
        )(SelectWidget),
    },
    established_year: {
        title: 'Year of Establishment',
        name: 'established_year',
        component: compose(
            mapProps(({onChange, value}) => ({
                onChange,
                value,
                name: 'established_year',
                placeholder: 'YYYY',
                maxLength: 4,
                type: 'number',
                inputMode: 'number',
            })),
            pure
        )(Input),
    },
    business_type: {
        title: 'Nature of Business',
        name: 'business_type',
        component: compose(
            connect((state) => ({
                data_object: _.get(
                    getCacheSelector(state, 'appConfig'),
                    'business_types',
                    []
                ),
            })),
            withSelectWidget,
            pure
        )(SelectWidget),
    },
    company_type: {
        title: 'Legal Status of Firm',
        name: 'company_type',
        component: compose(
            connect((state) => ({
                data_object: _.get(
                    getCacheSelector(state, 'appConfig'),
                    'company_types',
                    []
                ),
            })),
            withSelectWidget,
            pure
        )(SelectWidget),
    },
    annual_turnover: {
        title: 'Annual Turnover',
        name: 'annual_turnover',
        component: compose(
            connect((state) => ({
                data_object: _.get(
                    getCacheSelector(state, 'appConfig'),
                    'annual_turnover',
                    []
                ),
            })),
            withSelectWidget,
            pure
        )(SelectWidget),
    },
    website: {
        title: 'website',
        name: 'website',
        component: compose(
            mapProps((props) => ({
                type: 'url',
                placeholder: 'www.yourwebsite.com...',
                inputMode: 'url',
                ...props,
            })),
            pure
        )(Input),
    },
    facebook: {
        title: 'facebook',
        name: 'facebook',
        component: compose(
            mapProps((props) => ({
                type: 'url',
                placeholder: 'FB url...',
                inputMode: 'url',
                ...props,
            })),
            pure
        )(Input),
    },
    linkedin: {
        title: 'linkedin',
        name: 'linkedin',
        component: compose(
            mapProps((props) => ({
                type: 'url',
                placeholder: 'LinkedIn url...',
                inputMode: 'url',
                ...props,
            })),
            pure
        )(Input),
    },
    twitter: {
        title: 'twitter',
        name: 'twitter',
        component: compose(
            mapProps((props) => ({
                type: 'url',
                placeholder: 'Twitter url...',
                inputMode: 'url',
                ...props,
            })),
            pure
        )(Input),
    },
}

const groupFields = {
    video_url__website: {
        title: 'video_url && website',
        render: (item) => {
            const {video_url, website} = item ?? {}
            return (
                <Translate>
                    {t => renderIfElse(
                        _.some([video_url, website]),
                        <div className="flex flex-col gap-1">
                            <ItemInformation
                                showIcon={true} className="italic tracking-wider"
                                label="website"
                                value={<a href={website} target="_blank">{website}</a>}
                            />
                            <ItemInformation
                                showIcon={true} label="video url"
                                value={<a href={video_url} target="_blank">{video_url}</a>}
                                className="italic tracking-wider"
                            />
                        </div>,
                        <span className="text-color-400 tracking-wide italic">
                            {`${t('no data is available now')}...`}
                        </span>
                    )}
                </Translate>
            )
        },
        formSchemas: [
            {
                title: 'website',
                children: [
                    {
                        name: 'website',
                        component: compose(
                            mapProps((props) => ({
                                type: 'url',
                                placeholder: 'www.yourwebsite.com...',
                                inputMode: 'url',
                                ...props,
                            })),
                            pure
                        )(Input),
                    }
                ]
            },
            {
                title: 'video url',
                children: [
                    {
                        name: 'video_url',
                        component: compose(
                            mapProps((props) => ({
                                type: 'url',
                                placeholder: 'www.youtube.com...',
                                inputMode: 'url',
                                ...props,
                            })),
                            pure
                        )(Input),
                    }
                ]
            }
        ],
        onValidate: (values, t) => {
            const { website, video_url } = values ?? {}
            const validate = (value, keyName, t) => {
                const rules = [
                    urlFormat(t)
                ].filter(Boolean)
                const message = runRules(value, rules)
                if(message) return {[keyName]: message}
            }
            return {
                ...validate(website, 'website', t),
                ...validate(video_url, 'video_url', t),
            }
        }
    },
    linked_pages: {
        title: 'Linked Pages',
        render: (item) => {
            const { linked_pages = [] } = item ?? {}
            return (
                <Translate>
                    {t => (
                        <div className="flex flex-col gap-1">
                            {linked_pages.map((e, i) => (
                                <ItemInformation
                                    showIcon={true} className="italic tracking-wider"
                                    label={e?.page}
                                    value={
                                        <a href={e?.url} target="_blank">{e?.url}</a>
                                    }
                                />
                            ))}
                        </div>
                    )}
                </Translate>
            )
        },
        formSchemas: [
            {
                title: 'Linked Pages',
                children: [
                    {
                        name: 'linked_pages',
                        component: compose(
                            mapProps(({name, value, onChange}) => ({
                                data: value,
                                classNames: ['flex items-center gap-3'],
                                renderWrapper: ({children}) => (
                                    <div className="flex flex-col gap-3">{children}</div>
                                ),
                                onChange: (value) => {
                                    onChange(createValue(name, value))
                                },
                            })),
                            pure
                        )(LinkedPages),
                    }
                ]
            }
        ],
        onValidate: (values, t) => {

            let errors = {}

            return errors
        },
    },
}

const OrganizationSettingProfile = () => {
    const t = useTranslate()
    const {organization: item} = useContext(OrganizationContainerContext)

    const {
        number_of_employees,
        established_year,
        business_type,
        company_type,
        annual_turnover,
        website,
    } = item ?? {}

    const initialValues = {
        tax_code: item.tax_code,
        country_id: item.country_id || 237,
        video_url: item.video_url,
        number_of_employees: '' + number_of_employees,
        established_year: '' + established_year,
        business_type: '' + business_type,
        company_type: '' + company_type,
        annual_turnover: '' + annual_turnover,
        website: '' + website,
        facebook: item.facebook,
        linkedin: item.linkedin,
        twitter: item.twitter,
        telegram: item.telegram,
        linked_pages: item.linked_pages,
    }

    const {onUpdate} = useOrganizationApi({})

    return (
        <PartialEditGroupWrapper
            groupFields={[
                groupFields.video_url__website,
                groupFields.linked_pages,
            ]}
            initialValues={initialValues}
            item={initialValues}
            onSubmit={({ ...values }) => {
                const {...changes} = shallowDiff(values, initialValues)
                onUpdate(
                    _.omitBy(
                        {
                            ...changes,
                            linked_pages: _.has(changes, 'linked_pages')
                                ? JSON.stringify(changes.linked_pages)
                                : undefined,
                        },
                        _.isUndefined
                    ),
                    {id: item?.id}
                )
            }}
        />
    )
}

export default OrganizationSettingProfile
